/* eslint-disable no-unused-vars */
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { Column, useColumnOrder, useGlobalFilter, usePagination, useSortBy, useTable, useExpanded } from "react-table";
import { AxiosResponse } from "axios";

type TableRequestOptions = {
  hiddenColumns?: string[];
  columnOrder?: string[];
  disablePagination?: boolean;
  params?: Record<string, any> | Function;
  deps?: Array<any>;
  filters?: (globalFilter: any) => Record<string, any>;
};

function useTableRequest(
  requestFn: (config?: any) => Promise<AxiosResponse<any, any>>,
  columns: Column[] | Column<any>[],
  options: TableRequestOptions = {},
) {
  const { hiddenColumns, columnOrder, params, filters, deps = [], disablePagination = false } = options;
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryPage = +(searchParams.get("page") || 0);
  // URL is the source of truth for which page to fetch. react-table's gotoPage
  // refuses forward navigation when pageIndex >= pageCount, so after a tab
  // switch pageIndex can lag behind the URL until pageCount catches up.
  const page = queryPage || 1;
  const requestAbortController = useRef<AbortController | null>(null);
  // Skip the initial mount so returning via back (?page=N) is not reset to page 1.
  const isFirstGlobalFilterEffect = useRef(true);

  const instance = useTable(
    {
      initialState: {
        globalFilter: {},
        ...(!disablePagination && {
          pageSize: +(Cookies.get("listing_sizeperpage") || 10),
          pageIndex: queryPage ? queryPage - 1 : 0,
        }),
        ...(hiddenColumns && { hiddenColumns }),
        ...(columnOrder && { columnOrder }),
      },
      columns,
      data,
      manualSortBy: true,
      manualGlobalFilter: true,
      autoResetHiddenColumns: false,
      autoResetPage: false,
      ...(!disablePagination && {
        manualPagination: true,
        pageCount: Math.ceil(totalData / +(Cookies.get("listing_sizeperpage") || 10)),
      }),
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useColumnOrder,
  );

  const makeRequest = useCallback(
    (abortController?: AbortController) => {
      const [sortOption] = instance.state.sortBy;

      setFetching(true);
      requestFn({
        params: {
          ...(!disablePagination && {
            page_size: instance.state.pageSize,
            page,
          }),
          ...(sortOption && {
            sort_by: `${sortOption.id}:${sortOption.desc ? "DESC" : "ASC"}`,
          }),
          ...(params && typeof params === "function" ? params(instance) : params),
          ...(filters && filters(instance.state.globalFilter)),
        },
        ...(abortController && { signal: abortController.signal }),
      })
        .then((res) => {
          setData(res.data.results);
          setTotalData(res.data.count);
          setFetching(false);
        })
        .catch((err) => {
          if (err?.name !== "CanceledError") setFetching(false);
        });
    },
    [
      disablePagination,
      instance.state.sortBy,
      instance.state.globalFilter,
      instance.state.pageSize,
      page,
      ...deps,
    ],
  );

  const refetchRequest = (isDelete = false) => {
    if (isDelete && data.length === 1 && queryPage > 1) {
      searchParams.set("page", (queryPage - 1).toString());
      setSearchParams(searchParams);
    } else {
      makeRequest();
    }
  };

  useEffect(() => {
    requestAbortController.current = new window.AbortController();
    makeRequest(requestAbortController.current);

    return () => {
      if (requestAbortController.current) {
        requestAbortController.current.abort();
      }
    };
  }, [makeRequest]);

  // Re-run when totalData/pageCount updates so a previously rejected forward
  // gotoPage (e.g. restoring page 8 while still on a 2-page tab's pageCount) can succeed.
  // useLayoutEffect avoids a one-frame flash of the old pageIndex in the pagination UI.
  useLayoutEffect(() => {
    if (!disablePagination) {
      instance.gotoPage(queryPage ? queryPage - 1 : 0);
    }
  }, [queryPage, disablePagination, totalData]);

  useEffect(() => {
    if (isFirstGlobalFilterEffect.current) {
      isFirstGlobalFilterEffect.current = false;
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("page", "1");
    setSearchParams(nextParams);
  }, [instance.state.globalFilter]);

  return { instance, fetching, totalData, refetch: refetchRequest };
}

export default useTableRequest;
